'use client';

import { StrictMode, useState, useCallback, ReactNode } from 'react';
import { GoogleMapsProvider as BaseGoogleMapsProvider } from '@ubilabs/google-maps-react-hooks';

import { useI18n } from '@northladder/i18n';

import { GOOGLE_MAPS_API_KEY, GOOGLE_MAPS_MAP_ID } from '../../constants';
import { TMapNode, IBaseGoogleMapLocationOptions } from '../../types';

type TNodeRef = (node: TMapNode) => void;

type TGoogleMapProviderProps = Pick<
  IBaseGoogleMapLocationOptions,
  'regionKey'
> & {
  mapZoom?: number;
  renderMapContainer: (ref: TNodeRef) => ReactNode;
};

/**
 * -----------------------------------------------------------------------------
 * This is a wrapper around the base map provider component that abstract away
 * map setup logic.
 */
export function GoogleMapsProvider({
  renderMapContainer,
  regionKey,
  mapZoom,
}: TGoogleMapProviderProps) {
  const { locale } = useI18n();

  const [mapContainer, setMapContainer] = useState<TMapNode>(null);

  const mapRef = useCallback((node: TMapNode) => {
    if (node) {
      setMapContainer(node);
    }
  }, []);

  const coordinates = {
    lat: 25.2253416,
    lng: 55.3947848,
    zoom: 11,
    title: 'United Arab Emirates',
  };
  const { lat, lng, zoom } = coordinates;

  /**
   * Add more map options here
   * `center` and `zoom` are required for every map to be displayed
   */
  const mapOptions = {
    center: { lat, lng },
    zoom: mapZoom || zoom,
    mapId: GOOGLE_MAPS_MAP_ID || undefined,
    disableDefaultUI: false,
    zoomControl: true,
    zoomControlOptions: {
      position: 3, // Top right corner
    },
  };

  const googleMapsAPIParameters = {
    googleMapsAPIKey: GOOGLE_MAPS_API_KEY,
    language: locale, // 'ae', 'ksa' TODO: Revise this
    region: regionKey, // 'AE', 'SA', 'IQ', etc.
  };

  return (
    <BaseGoogleMapsProvider
      libraries={['places']}
      mapContainer={mapContainer}
      mapOptions={mapOptions}
      {...googleMapsAPIParameters}
    >
      <StrictMode>{renderMapContainer(mapRef)}</StrictMode>
    </BaseGoogleMapsProvider>
  );
}
